import {Draft, produce} from 'immer';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
// eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
import {unstable_batchedUpdates} from 'react-dom';
import {applyNodeChanges, Connection, Edge, Node, ReactFlowInstance, useStoreApi} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {getRouterNodeIdsBetweenNodes} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {ProcessesPluginBase} from 'processes/graph-builder-processes/platform/ProcessesPluginBase';
import {isNewEdgeValidForFlowchart} from 'processes/modals/utils/TransitionModalValidations';
import {
  FlowchartChangeGenerator,
  FlowchartChanges,
  UseFlowchartStateParams,
  UseFlowchartStateReturn,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';
import isFocusSupercededByModal from 'ui-library/modals/isFocusSupercededByModal';
import {ElkEdgeData} from 'ui-library/reactflow/edges/edgeOptions';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import {COMMON_NODE_WIDTH} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import runModal from 'ui-library/runModal';
import * as keyboard from 'util/Keyboard';
import {omit} from 'util/omitKey';

import {AddNodeOption, getAddNodeOptions} from '../AddNodeDropdown';
import buildInteractiveFlowchartFromStaticData from '../buildInteractiveFlowchartFromStaticData';
import {
  buildControlFlowEdge,
  generateDeterministicEdgeId,
} from '../buildStaticDataFromFlowchartConfigGraphQL';
import {getEdgeTypeFromHandleId} from '../flowchartIdentifierUtils';
import getAllNodeDetailsSidepanelTabsForTaskNode from '../getAllNodeDetailsSidepanelTabsForTaskNode';
import FlowchartRemoveNodeModal from '../modals/FlowchartRemoveNodeModal';
import {
  FlowchartEdge,
  FlowchartStaticEdgeData,
  INTERACTION_FIELDS,
  ProcessFlowchartEdgeType,
} from '../processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  isInteractivePlaceholderNode,
  isInteractiveTaskNode,
  isStaticRouterNodeData,
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  TaskSchemaStaticNodeData,
} from '../processFlowchartConfigEditorNodeTypes';
import {
  FlowchartCustomMappingDataChange,
  FlowchartCustomMappingDataChangeType,
  FlowchartEdgeChange,
  FlowchartEdgeChangeType,
  FlowchartMetadataOptionsChange,
  FlowchartMetadataOptionsChangeType,
  FlowchartNodeChange,
  FlowchartNodeChangeType,
  FlowchartOptions,
  FlowchartTransitionSourceChange,
  FlowchartTransitionSourceChangeType,
} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigWithPartialMetadata} from '../redux/FlowchartConfigStateManager';
import {FlowchartConfigMetadata} from '../redux/reduceFlowchartConfig';
import {useFlowchartSidepanelManagerContext} from '../right-sidepanel/FlowchartSidepanelUnsavedChangesContext';
import {SidepanelChange, SidepanelType} from '../right-sidepanel/useFlowchartSidepanelManager';
import {getNodeIdPairFromTransitionSourceKey} from '../TransitionSourceUtils';
import {centerNodeInVisibleCanvas} from './centerNodeInVisibleCanvas';
import {
  mirrorCustomMappingData,
  mirrorTransitionSource,
  processEdgeChangesWithMirroring,
} from './edgeMirroringUtils';
import getNodeChangesForNewEdge from './getNodeChangesForNewEdge';
import getRouterConfigChangesAfterEdgeChange from './getRouterConfigChangesAfterEdgeChange';

/**
 * Validates edge changes and filters out invalid changes.
 */
function validateEdgeChanges(
  edgeChanges: Array<FlowchartEdgeChange>,
  existingEdges: Array<FlowchartStaticEdgeData>
): Array<FlowchartEdgeChange> {
  return edgeChanges.filter((edgeChange) => {
    // Only validate 'add' changes, others are always valid
    if (edgeChange.type !== FlowchartEdgeChangeType.ADD || !edgeChange.item) {
      return true;
    }

    return isNewEdgeValidForFlowchart(edgeChange.item, existingEdges);
  });
}

function getOrphanedEdgeIdsAfterNodeRemoval(
  draft: Draft<FlowchartConfigWithMetadata>,
  removedNodeId: string
): Array<string> {
  return draft.staticFlowchart.edgesStaticData
    .filter((edge) => edge.source === removedNodeId || edge.target === removedNodeId)
    .map((edge) => edge.id);
}

export function getEmptyFlowchartChanges(): FlowchartChanges {
  return {
    nodeChanges: [],
    edgeChanges: [],
    transitionSourceChanges: [],
    customMappingChanges: [],
    metadataOptionsChanges: [],
  };
}

function applyFlowchartNodeChanges(
  nodeChanges: Array<FlowchartNodeChange>,
  draft: Draft<FlowchartConfigWithMetadata>,
  setNodePositions: React.Dispatch<React.SetStateAction<Record<string, {x: number; y: number}>>>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  nodeChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartNodeChangeType.ADD:
        if (change.item != null) {
          const newNodeData: StaticFlowchartNodeData = extractStaticData(change.item.data);
          draft.staticFlowchart.nodesStaticData.push(newNodeData);
          // Set initial position in local state
          setNodePositions((prevPositions) => ({
            ...prevPositions,
            [change.item.id]: change.item.position || {x: 0, y: 0},
          }));
        }
        break;
      case FlowchartNodeChangeType.POSITION:
        if (change.position) {
          setNodePositions((prevPositions) => ({
            ...prevPositions,
            [change.id]: change.position as {x: number; y: number},
          }));
        }
        break;
      case FlowchartNodeChangeType.SELECT: {
        // Only modify the Set if the change would actually change it (prevents unnecessary re-renders)
        const isCurrentlySelected = draft.metadata.interactionState.selectedNodeIds.has(change.id);
        if (change.selected && !isCurrentlySelected) {
          draft.metadata.interactionState.selectedNodeIds.add(change.id);
        } else if (!change.selected && isCurrentlySelected) {
          draft.metadata.interactionState.selectedNodeIds.delete(change.id);
        }
        break;
      }
      case FlowchartNodeChangeType.SELECT_ROW: {
        // Only modify the Set if the change would actually change it (prevents unnecessary re-renders)
        const isCurrentlySelected = draft.metadata.interactionState.selectedRowIds.has(change.rowId);
        if (change.selected && !isCurrentlySelected) {
          draft.metadata.interactionState.selectedRowIds.add(change.rowId);
        } else if (!change.selected && isCurrentlySelected) {
          draft.metadata.interactionState.selectedRowIds.delete(change.rowId);
        }
        break;
      }
      case FlowchartNodeChangeType.REMOVE:
        assert(
          draft.staticFlowchart.nodesStaticData.some((node) => node.id === change.id),
          'Node does not exist in static data'
        );
        draft.staticFlowchart.nodesStaticData = draft.staticFlowchart.nodesStaticData.filter(
          (node) => node.id !== change.id
        );
        draft.metadata.interactionState.selectedNodeIds.delete(change.id);
        // Clean up local position state
        setNodePositions((prevPositions) => {
          const newPositions = {...prevPositions};
          delete newPositions[change.id];
          return newPositions;
        });

        // Post-processing
        sideEffects.edgeChanges.push(
          ...getOrphanedEdgeIdsAfterNodeRemoval(draft, change.id).map((edgeId) => ({
            type: FlowchartEdgeChangeType.REMOVE as const,
            id: edgeId,
          }))
        );
        break;
      case FlowchartNodeChangeType.REPLACE: {
        const nodeToReplace = draft.staticFlowchart.nodesStaticData.find((node) => node.id === change.id);
        if (nodeToReplace) {
          const newNodeData: StaticFlowchartNodeData = extractStaticData(change.data);
          Object.assign(nodeToReplace, newNodeData);
        }
        break;
      }
      case FlowchartNodeChangeType.RESET:
        // TODO: Explore if reset changes need to be handled?
        break;
      default:
        assert(false, 'Unexpected node change type', {change});
    }
  });

  return sideEffects;
}

function applyFlowchartEdgeChanges(
  edgeChanges: Array<FlowchartEdgeChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  // Pre-processing
  const allEdgeChanges = processEdgeChangesWithMirroring(edgeChanges, draft, {
    customMappingDataByEdgeId: draft.metadata.customMappingDataByEdgeId,
    onChangeCustomMappingData: (edgeId, newCustomMappingData) => {
      draft.metadata.customMappingDataByEdgeId[edgeId] = newCustomMappingData;
    },
    transitionSourceByNodeIdPair: draft.metadata.transitionSourceByNodeIdPair,
    onChangeTransitionSource: (key, newTransitionSource) => {
      draft.metadata.transitionSourceByNodeIdPair[key] = newTransitionSource;
    },
  });
  const validatedEdgeChanges = validateEdgeChanges(allEdgeChanges, draft.staticFlowchart.edgesStaticData);

  validatedEdgeChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartEdgeChangeType.ADD:
        if (change.item != null) {
          assert(change.item.sourceHandle != null, 'Source handle is required');
          assert(change.item.targetHandle != null, 'Target handle is required');
          assert(change.item.type != null, 'Type is required');
          const newEdge: FlowchartStaticEdgeData = {
            id: change.item.id,
            source: change.item.source,
            target: change.item.target,
            sourceHandle: change.item.sourceHandle,
            targetHandle: change.item.targetHandle,
            type: change.item.type as ProcessFlowchartEdgeType,
          };
          draft.staticFlowchart.edgesStaticData.push(newEdge);

          sideEffects.nodeChanges.push(...getNodeChangesForNewEdge(draft.staticFlowchart, newEdge));
        }
        break;
      case FlowchartEdgeChangeType.SELECT:
        if (change.selected) {
          draft.metadata.interactionState.selectedEdgeIds.add(change.id);
        } else {
          draft.metadata.interactionState.selectedEdgeIds.delete(change.id);
        }
        break;
      case FlowchartEdgeChangeType.REMOVE:
        draft.staticFlowchart.edgesStaticData = draft.staticFlowchart.edgesStaticData.filter(
          (edge) => edge.id !== change.id
        );
        draft.metadata.interactionState.selectedEdgeIds.delete(change.id);

        sideEffects.customMappingChanges.push({
          type: FlowchartCustomMappingDataChangeType.REMOVE,
          id: change.id,
        });

        sideEffects.nodeChanges.push(
          ...getRouterConfigChangesAfterEdgeChange(
            draft.staticFlowchart,
            FlowchartEdgeChangeType.REMOVE,
            change.id
          )
        );
        break;
      case FlowchartEdgeChangeType.LABEL: {
        draft.metadata.interactionState.edgeLabelsByEdgeId[change.id] = change.label;
        const targetEdge = draft.staticFlowchart.edgesStaticData.find((edge) => edge.id === change.id);
        assert(targetEdge != null, 'Target edge should not be null');
        targetEdge.label = change.label;
        break;
      }
      case FlowchartEdgeChangeType.RESET:
        // TODO: Explore if reset changes need to be handled?
        break;
      default:
        assert(false, 'Unexpected edge change type', {change});
    }
  });

  return sideEffects;
}

function applyTransitionSourceChanges(
  transitionSourceChanges: Array<FlowchartTransitionSourceChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  transitionSourceChanges.forEach((change) => {
    const originalTransitionSource = draft.metadata.transitionSourceByNodeIdPair[change.id];
    draft.metadata.transitionSourceByNodeIdPair[change.id] = change.transitionSource;

    if (change.transitionSource !== originalTransitionSource) {
      const {sourceNodeId, targetNodeId} = getNodeIdPairFromTransitionSourceKey(change.id);
      const routerNodeIds = getRouterNodeIdsBetweenNodes(
        {
          nodes: draft.staticFlowchart.nodesStaticData.map((node) => ({id: node.id, data: node})),
          edges: draft.staticFlowchart.edgesStaticData,
        },
        sourceNodeId,
        targetNodeId
      );
      for (const routerNodeId of routerNodeIds) {
        const routerNode = draft.staticFlowchart.nodesStaticData.find((node) => node.id === routerNodeId)!;
        assert(isStaticRouterNodeData(routerNode), 'Expected router node');
        // When the transition source is changed, we need to clear the expressions on all router nodes between
        // the two nodes because the router configurations depend on the transition source.
        // We do not need to clear the default function spec because it does not have an expression associated
        // with it.
        const newData = produce(routerNode, (draftRouterNode) => {
          draftRouterNode.routerConfig.functionSpecs = draftRouterNode.routerConfig.functionSpecs.map(
            (spec) => ({
              ...spec,
              expression: null,
              isConfigured: false,
            })
          );
        });
        sideEffects.nodeChanges.push({
          id: routerNodeId,
          type: FlowchartNodeChangeType.REPLACE,
          data: newData,
        });
      }
      mirrorTransitionSource(
        change.id,
        change.transitionSource,
        draft.staticFlowchart.edgesStaticData,
        draft.staticFlowchart.nodesStaticData,
        (key, transitionSource) => {
          sideEffects.transitionSourceChanges.push({
            type: FlowchartTransitionSourceChangeType.REPLACE,
            id: key,
            transitionSource,
          });
        },
        (key) => {
          delete draft.metadata.transitionSourceByNodeIdPair[key];
        }
      );
    }
  });

  return sideEffects;
}

function applyCustomMappingChanges(
  customMappingChanges: Array<FlowchartCustomMappingDataChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  customMappingChanges.forEach((change) => {
    const customMappingValue =
      change.type === FlowchartCustomMappingDataChangeType.ADD ||
      change.type === FlowchartCustomMappingDataChangeType.REPLACE
        ? change.customMapping
        : null;

    switch (change.type) {
      case FlowchartCustomMappingDataChangeType.ADD:
        draft.metadata.customMappingDataByEdgeId[change.id] = change.customMapping;
        break;
      case FlowchartCustomMappingDataChangeType.REMOVE:
        delete draft.metadata.customMappingDataByEdgeId[change.id];
        break;
      case FlowchartCustomMappingDataChangeType.REPLACE:
        draft.metadata.customMappingDataByEdgeId[change.id] = change.customMapping;
        break;
      default:
        assert(false, 'Unexpected custom mapping data change type', {change});
        break;
    }

    mirrorCustomMappingData(
      change.id,
      customMappingValue,
      draft.staticFlowchart.edgesStaticData,
      draft.staticFlowchart.nodesStaticData,
      (edgeId, customMapping) => {
        draft.metadata.customMappingDataByEdgeId[edgeId] = customMapping;
      },
      (edgeId) => {
        delete draft.metadata.customMappingDataByEdgeId[edgeId];
      }
    );
  });

  return sideEffects;
}

function applyMetadataOptionsChanges(
  metadataOptionsChanges: Array<FlowchartMetadataOptionsChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  metadataOptionsChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartMetadataOptionsChangeType.UPDATE:
        draft.metadata.options = {...draft.metadata.options, ...change.options};
        break;
      default:
        assert(false, 'Unexpected options change type', {change});
    }
  });

  return sideEffects;
}

/**
 * Detects which parts of the flowchart state have changed and returns only the changed parts.
 *
 * Uses Immer's structural sharing: unchanged parts keep their original references, while changed parts get new references.
 * This enables fast reference equality checks to identify what actually changed.
 *
 * We return only changed parts (not the entire state) so reduceFlowchartConfig can merge these changes with the latest server state.
 */
function detectChangedParts(
  original: FlowchartConfigWithMetadata,
  updated: FlowchartConfigWithMetadata
): FlowchartConfigWithPartialMetadata | null {
  // Early short-circuit: if both references are the same, nothing changed
  // (This only happens if produce() was never called)
  if (original === updated) {
    return null;
  }

  const staticFlowchartChanged = updated.staticFlowchart !== original.staticFlowchart;

  const interactionStateChanged = updated.metadata.interactionState !== original.metadata.interactionState;
  const transitionSourceChanged =
    updated.metadata.transitionSourceByNodeIdPair !== original.metadata.transitionSourceByNodeIdPair;
  const customMappingChanged =
    updated.metadata.customMappingDataByEdgeId !== original.metadata.customMappingDataByEdgeId;
  const metadataOptionsChanged = updated.metadata.options !== original.metadata.options;

  const anyMetadataChanged =
    interactionStateChanged || transitionSourceChanged || customMappingChanged || metadataOptionsChanged;

  // Early short-circuit: if nothing actually changed, return null
  if (!staticFlowchartChanged && !anyMetadataChanged) {
    return null;
  }

  // Build the result object based on what actually changed
  const buildMetadataChanges = (): Partial<FlowchartConfigMetadata> => {
    const metadataChanges: Partial<FlowchartConfigMetadata> = {};

    if (interactionStateChanged) {
      metadataChanges.interactionState = updated.metadata.interactionState;
    }

    if (transitionSourceChanged) {
      metadataChanges.transitionSourceByNodeIdPair = updated.metadata.transitionSourceByNodeIdPair;
    }

    if (customMappingChanged) {
      metadataChanges.customMappingDataByEdgeId = updated.metadata.customMappingDataByEdgeId;
    }

    if (metadataOptionsChanged) {
      metadataChanges.options = updated.metadata.options;
    }

    return metadataChanges;
  };

  if (staticFlowchartChanged && anyMetadataChanged) {
    return {
      staticFlowchart: updated.staticFlowchart,
      flowchartConfigMetadata: buildMetadataChanges(),
    };
  } else if (staticFlowchartChanged) {
    return {
      staticFlowchart: updated.staticFlowchart,
    };
  } else {
    return {
      flowchartConfigMetadata: buildMetadataChanges(),
    };
  }
}

/**
 * Extracts the static data from the data object of a flowchart node.
 * @param nodeData The data object on a FlowchartNode.
 * @returns The static data of the node.
 */
export function extractStaticData(nodeData: FlowchartNode['data']): StaticFlowchartNodeData {
  return omit(
    nodeData,
    ...(INTERACTION_FIELDS as unknown as Array<keyof FlowchartNode['data']>)
  ) as StaticFlowchartNodeData;
}

export function extractStaticEdgeData(edge: FlowchartEdge): FlowchartStaticEdgeData {
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    type: edge.type,
    label: edge.label,
  };
}

function combineFlowchartChanges(
  sideEffects: FlowchartChanges,
  additionalChanges: Partial<FlowchartChanges>
): FlowchartChanges {
  return {
    nodeChanges: [...sideEffects.nodeChanges, ...(additionalChanges.nodeChanges ?? [])],
    edgeChanges: [...sideEffects.edgeChanges, ...(additionalChanges.edgeChanges ?? [])],
    transitionSourceChanges: [
      ...sideEffects.transitionSourceChanges,
      ...(additionalChanges.transitionSourceChanges ?? []),
    ],
    customMappingChanges: [
      ...sideEffects.customMappingChanges,
      ...(additionalChanges.customMappingChanges ?? []),
    ],
    metadataOptionsChanges: [
      ...sideEffects.metadataOptionsChanges,
      ...(additionalChanges.metadataOptionsChanges ?? []),
    ],
  };
}

/**
 * Core logic for applying flowchart changes to static data, metadata, and interactive state.
 */
export function applyFlowchartChangesCore(
  currentState: FlowchartConfigWithMetadata,
  changes: Partial<FlowchartChanges>,
  setNodePositions: React.Dispatch<React.SetStateAction<Record<string, {x: number; y: number}>>>,
  plugin?: ProcessesPluginBase
): FlowchartConfigWithPartialMetadata | null {
  const {
    nodeChanges = [],
    edgeChanges = [],
    transitionSourceChanges = [],
    customMappingChanges = [],
    metadataOptionsChanges = [],
  } = changes;

  let currSideEffects: FlowchartChanges = {
    nodeChanges,
    edgeChanges,
    transitionSourceChanges,
    customMappingChanges,
    metadataOptionsChanges,
  };

  const getDoesSideEffectsHaveChanges = (sideEffects: FlowchartChanges): boolean =>
    Object.values(sideEffects).some((changeArray) => changeArray.length > 0);

  if (!getDoesSideEffectsHaveChanges(currSideEffects)) {
    return null;
  }

  const updatedFlowchartAndMetadata = produce(currentState, (draft) => {
    let cumulativeChanges: FlowchartChanges = getEmptyFlowchartChanges();

    do {
      cumulativeChanges = combineFlowchartChanges(cumulativeChanges, currSideEffects);

      const nodeSideEffects = applyFlowchartNodeChanges(currSideEffects.nodeChanges, draft, setNodePositions);
      const edgeSideEffects = applyFlowchartEdgeChanges(currSideEffects.edgeChanges, draft);
      const transitionSourceSideEffects = applyTransitionSourceChanges(
        currSideEffects.transitionSourceChanges,
        draft
      );
      const customMappingSideEffects = applyCustomMappingChanges(currSideEffects.customMappingChanges, draft);
      const metadataOptionsSideEffects = applyMetadataOptionsChanges(
        currSideEffects.metadataOptionsChanges,
        draft
      );

      const allSideEffects = [
        nodeSideEffects,
        edgeSideEffects,
        transitionSourceSideEffects,
        customMappingSideEffects,
        metadataOptionsSideEffects,
      ];
      currSideEffects = allSideEffects.reduce(
        (acc, curr) => ({
          nodeChanges: [...acc.nodeChanges, ...curr.nodeChanges],
          edgeChanges: [...acc.edgeChanges, ...curr.edgeChanges],
          transitionSourceChanges: [...acc.transitionSourceChanges, ...curr.transitionSourceChanges],
          customMappingChanges: [...acc.customMappingChanges, ...curr.customMappingChanges],
          metadataOptionsChanges: [...acc.metadataOptionsChanges, ...curr.metadataOptionsChanges],
        }),
        getEmptyFlowchartChanges()
      );

      // Collect healing side effects from the validator
      if (plugin != null) {
        const healingChanges = plugin.flowchartValidator.healFlowchart(
          combineFlowchartChanges(cumulativeChanges, currSideEffects)
        )(draft);
        currSideEffects = combineFlowchartChanges(currSideEffects, healingChanges);
      }
    } while (getDoesSideEffectsHaveChanges(currSideEffects));
  });

  return detectChangedParts(currentState, updatedFlowchartAndMetadata);
}

/**
 * Contains the core flowchart state management logic for the horizontal flowchart.
 *
 * Main features:
 * - Apply changes to the flowchart and/or metadata
 * - Handle right sidepanel state management
 * - Handle reactflow actions related to nodes + edges
 *
 * Core concepts:
 * - Minimal flowchart representations to actively track:
 *      We should rarely, if ever, need to mutate or interact with the interactive nodes/edges directly,
 *      which gets sent to reactflow for rendering. Instead, we change the static flowchart, metadata and interactive
 *      state trackers as needed, and the interactive flowchart is rebuilt from those sources of truth automatically.
 * - All change management is funneled through the applyFlowchartChanges function:
 *      This allows for centralizing side effects and autohealing logic related to flowchart changes.
 *

 * @returns The flowchart state management logic.
 */
const useFlowchartState = (params: UseFlowchartStateParams): UseFlowchartStateReturn => {
  const {
    staticFlowchart,
    flowchartConfigMetadata,
    validationErrors,
    setFlowchartAndMetadata,
    addNodeParams,
    reactFlowInstance,
    reactFlowWrapper,
    leftSidepanelRef,
    rightSidepanelRef,
  } = params;

  const {flowchartTypeName} = flowchartConfigMetadata;
  const plugin = PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(flowchartTypeName);

  const locked = ReactFlowLockedContext.useContext() ?? false;
  const reactFlowStore = useStoreApi();

  const getDefaultNodeDimensions = useCallback((): {width: number; height: number} => {
    const fallbackNodeHeight = COMMON_NODE_WIDTH * 2;
    return {
      width: COMMON_NODE_WIDTH,
      height: fallbackNodeHeight,
    };
  }, []);

  const getMeasuredDimensionsFromStore = useCallback(
    (nodeId: string): {width: number; height: number} => {
      const {nodeInternals} = reactFlowStore.getState();
      const nodeInternal = nodeInternals.get(nodeId);

      const width = nodeInternal?.width;
      const height = nodeInternal?.height;
      if (width != null && height != null && width > 0 && height > 0) {
        return {width, height};
      }

      return getDefaultNodeDimensions();
    },
    [reactFlowStore, getDefaultNodeDimensions]
  );

  const [interactiveNodes, setInteractiveNodes] = useState<Array<FlowchartNode>>([]);
  const [interactiveEdges, setInteractiveEdges] = useState<Array<FlowchartEdge>>([]);
  // By node ID
  const [nodePositions, setNodePositions] = useState<Record<string, {x: number; y: number}>>({});

  // Ref to track current node positions for use in rebuilds during drag operations
  // This allows us to preserve drag positions without triggering full flowchart rebuilds
  const currentNodePositionsRef = useRef<Record<string, {x: number; y: number}>>({});

  const sidepanelManager = useFlowchartSidepanelManagerContext();
  const isLeftSidepanelOpen = useMemo(
    () => sidepanelManager.getEffectiveState().openSidepanelType === SidepanelType.FLOWCHART_SETTINGS,
    [sidepanelManager]
  );

  const [_sourceNodeAndNewNodeBeingAdded, setSourceNodeAndNewNodeBeingAdded] = useState<
    [string, FlowchartNode] | null
  >(null);

  const [placeholderNodeIdBeingAdded, setPlaceholderNodeBeingAdded] = useState<string | null>(null);

  const applyFlowchartChanges = useCallback(
    (
      changeGenerator: FlowchartChangeGenerator,
      saveImmediately: boolean = false,
      skipSettingDirtyState: boolean = false
    ): Promise<FlowchartConfigMetadata | null> =>
      setFlowchartAndMetadata(
        (currentState) => {
          if (!currentState) {
            return null;
          }

          const changes = changeGenerator(currentState);

          // When locked, allow selection changes so the sidepanel can open in read-only mode,
          // but block all other changes (add/remove/replace nodes, edge changes, metadata changes)
          if (locked) {
            const onlySelection =
              (changes.nodeChanges ?? []).every((c) => c.type === FlowchartNodeChangeType.SELECT) &&
              (changes.edgeChanges ?? []).every((c) => c.type === FlowchartEdgeChangeType.SELECT) &&
              !changes.transitionSourceChanges?.length &&
              !changes.customMappingChanges?.length;
            if (!onlySelection) {
              return null;
            }
          }
          return applyFlowchartChangesCore(currentState, changes, setNodePositions, plugin);
        },
        saveImmediately,
        skipSettingDirtyState
      ),
    [setFlowchartAndMetadata, locked, plugin]
  );

  // Reverts node + field selection back to a specific node and field
  const revertNodeSelection = useCallback(
    (nodeId: string, fieldId: string | null) => {
      applyFlowchartChanges((flowchartConfigWithMetadata) => {
        const nodeChanges: Array<FlowchartNodeChange> = [];

        // Deselect any currently selected nodes
        const currentlySelectedNodeIds =
          flowchartConfigWithMetadata.metadata.interactionState.selectedNodeIds;
        currentlySelectedNodeIds.forEach((selectedId: string) => {
          if (selectedId !== nodeId) {
            nodeChanges.push({
              type: FlowchartNodeChangeType.SELECT,
              id: selectedId,
              selected: false,
            });
          }
        });

        nodeChanges.push({
          type: FlowchartNodeChangeType.SELECT,
          id: nodeId,
          selected: true,
        });

        return {nodeChanges};
      }).then(() => {
        setInteractiveNodes((currentNodes: Array<FlowchartNode>) =>
          currentNodes.map((node: FlowchartNode) => ({
            ...node,
            selected: node.id === nodeId,
          }))
        );
      });
    },
    [applyFlowchartChanges, setInteractiveNodes]
  );

  const openSidepanelAndSelectNode = useCallback(
    async (nodeId: string, fieldId: string | null = null, rowId: string | null = null): Promise<void> => {
      const {node: currentNode, fieldId: currentFieldId} = sidepanelManager.getEffectiveState();

      const success = await sidepanelManager.changeSidepanel({
        sidepanelChange: {
          sidepanel: SidepanelType.NODE_SETTINGS,
          nodeId,
          fieldId,
        },
      });

      if (!success) {
        // User chose "Keep Editing" - revert selection back to current node
        if (currentNode != null) {
          revertNodeSelection(currentNode.id, currentFieldId);
        }
        return;
      }

      applyFlowchartChanges((flowchartConfigWithMetadata) => {
        const nodeChanges: Array<FlowchartNodeChange> = [];

        // Deselect any currently selected nodes or rows
        flowchartConfigWithMetadata.metadata.interactionState.selectedNodeIds.forEach(
          (selectedId: string) => {
            if (selectedId !== nodeId) {
              nodeChanges.push({
                type: FlowchartNodeChangeType.SELECT,
                id: selectedId,
                selected: false,
              });
            }
          }
        );
        flowchartConfigWithMetadata.metadata.interactionState.selectedRowIds.forEach(
          (selectedRowId: string) => {
            if (selectedRowId !== rowId) {
              nodeChanges.push({
                type: FlowchartNodeChangeType.SELECT_ROW,
                id: nodeId,
                rowId: selectedRowId,
                selected: false,
              });
            }
          }
        );

        // Select the target node
        nodeChanges.push({
          type: FlowchartNodeChangeType.SELECT,
          id: nodeId,
          selected: true,
        });
        if (rowId != null) {
          nodeChanges.push({
            type: FlowchartNodeChangeType.SELECT_ROW,
            id: nodeId,
            rowId,
            selected: true,
          });
        }

        return {nodeChanges};
      });

      // Center the node in the visible canvas after sidepanel opens
      if (reactFlowInstance && reactFlowWrapper?.current) {
        // Use setTimeout to ensure DOM has updated after sidepanel opens
        setTimeout(() => {
          const nodePosition = nodePositions[nodeId];
          const dimensions = getMeasuredDimensionsFromStore(nodeId);
          if (nodePosition) {
            centerNodeInVisibleCanvas({
              reactFlowInstance,
              reactFlowWrapper,
              leftSidepanelRef: leftSidepanelRef ?? {current: null},
              rightSidepanelRef: rightSidepanelRef ?? {current: null},
              nodePosition,
              nodeWidth: dimensions.width,
              nodeHeight: dimensions.height,
            });
          }
        }, 0);
      }
    },
    [
      sidepanelManager,
      revertNodeSelection,
      applyFlowchartChanges,
      reactFlowInstance,
      reactFlowWrapper,
      leftSidepanelRef,
      rightSidepanelRef,
      nodePositions,
      getMeasuredDimensionsFromStore,
    ]
  );

  const doesNodeHaveRightSidepanelContent = useCallback(
    (nodeId: string): boolean => {
      const reactFlowNode = reactFlowInstance?.getNode(nodeId) as FlowchartNode | undefined;
      if (reactFlowNode == null) {
        return false;
      } else if (!isInteractiveTaskNode(reactFlowNode)) {
        return true;
      }

      const rightSidepanelTabs = getAllNodeDetailsSidepanelTabsForTaskNode(
        reactFlowNode,
        params.flowchartEditorConfigurator.platformFlowchartFeatures
      );
      return rightSidepanelTabs.length > 0;
    },
    [reactFlowInstance, params.flowchartEditorConfigurator]
  );

  const openSidepanelForNode = useCallback(
    (nodeId: string): void => {
      if (!doesNodeHaveRightSidepanelContent(nodeId)) {
        return;
      }
      openSidepanelAndSelectNode(nodeId, null, null);
    },
    [openSidepanelAndSelectNode, doesNodeHaveRightSidepanelContent]
  );

  const openSidepanelForNodeWithField = useCallback(
    (nodeId: string, fieldId: string, rowId: string): void => {
      if (!doesNodeHaveRightSidepanelContent(nodeId)) {
        return;
      }
      openSidepanelAndSelectNode(nodeId, fieldId, rowId);
    },
    [openSidepanelAndSelectNode, doesNodeHaveRightSidepanelContent]
  );

  const getAddNodeOptionsForRightHandle = useCallback(
    (sourceNodeId: string, reactFlowInstance: ReactFlowInstance): Array<AddNodeOption> | null =>
      getAddNodeOptions({
        flowchartEditorConfigurator: addNodeParams.flowchartEditorConfigurator,
        sourceNodeId,
        reactFlowInstance,
        singletonTaskSchemasByName: addNodeParams.singletonTaskSchemasByName,
        canAddExistingTaskSchema: addNodeParams.taskDefinitionsForSidepanel.length > 0,
        rootTaskSchema: addNodeParams.rootTaskSchema,
        openSidepanelForNode,
        openSidepanelForNodeWithField,
        onNodeChangesOnly: (nodeChangeGenerator) => {
          applyFlowchartChanges((flowchartConfig) => ({
            nodeChanges: nodeChangeGenerator(flowchartConfig),
            edgeChanges: [],
          }));
        },
        setSourceNodeAndNewNodeBeingAdded: ([sourceNodeId, newNode]: [string, FlowchartNode]) => {
          analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.NODE_ADDED_FROM_RIGHT_HANDLE_CLICK, {
            actionMethod: ActionMethod.BUTTON_CLICK,
            actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
            nodeType: newNode.data.nodeType,
            flowchartType: flowchartTypeName,
          });
          setSourceNodeAndNewNodeBeingAdded([sourceNodeId, newNode]);
        },
        didInitialFlowchartHaveChainedRouters: addNodeParams.didInitialFlowchartHaveChainedRouters,
      }),
    [
      addNodeParams,
      openSidepanelForNode,
      openSidepanelForNodeWithField,
      applyFlowchartChanges,
      flowchartTypeName,
    ]
  );

  const interactiveFlowchartOptions: FlowchartOptions = useMemo(
    () => ({
      onNodeHeaderClick: openSidepanelForNode,
      onFieldClick: openSidepanelForNodeWithField,
      onUpdateNodeData: (nodeChangeGenerator, saveImmediately, skipSettingDirtyState) =>
        applyFlowchartChanges(
          (flowchartConfig) => ({
            nodeChanges: nodeChangeGenerator(flowchartConfig),
          }),
          saveImmediately,
          skipSettingDirtyState
        ),
      getAddNodeOptionsForRightHandle,
      validationErrors,
    }),
    [
      openSidepanelForNode,
      openSidepanelForNodeWithField,
      getAddNodeOptionsForRightHandle,
      validationErrors,
      applyFlowchartChanges,
    ]
  );

  const [elkEdgeDataByEdgeId, setElkEdgeDataByEdgeId] = useState({});
  useEffect(() => {
    if (!staticFlowchart) {
      return;
    }

    // Use current positions from ref to preserve drag positions
    // Falls back to local state for nodes that don't exist yet (e.g., newly added)
    const positionsWithFallback = {
      ...nodePositions,
      ...currentNodePositionsRef.current,
    };

    const {nodes, edges} = buildInteractiveFlowchartFromStaticData({
      staticNodesData: staticFlowchart.nodesStaticData,
      staticEdgesData: staticFlowchart.edgesStaticData,
      options: interactiveFlowchartOptions,
      nodePositionsByNodeId: positionsWithFallback,
      selectedNodeIds: flowchartConfigMetadata.interactionState.selectedNodeIds,
      selectedRowIds: flowchartConfigMetadata.interactionState.selectedRowIds,
      selectedEdgeIds: flowchartConfigMetadata.interactionState.selectedEdgeIds,
      elkEdgeDataByEdgeId,
      validationErrors,
    });

    nodes.forEach((node) => {
      currentNodePositionsRef.current[node.id] = node.position;
    });

    setInteractiveNodes(nodes);
    setInteractiveEdges(edges);
    // Intentionally exclude nodePositions to avoid rebuild on every drag.
    // When staticFlowchart changes (e.g. node added), this effect runs and reads nodePositions for new nodes.
    // But we don't add it to deps because position-only changes shouldn't trigger rebuilds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    staticFlowchart,
    flowchartConfigMetadata.interactionState.selectedNodeIds,
    flowchartConfigMetadata.interactionState.selectedRowIds,
    flowchartConfigMetadata.interactionState.selectedEdgeIds,
    interactiveFlowchartOptions,
    elkEdgeDataByEdgeId,
    validationErrors,
  ]);

  // Handle adding nodes from right handle
  useEffect(() => {
    if (_sourceNodeAndNewNodeBeingAdded == null) {
      return;
    }
    const [sourceNodeId, newNode] = _sourceNodeAndNewNodeBeingAdded;
    // Clear the state immediately to prevent the effect from running again with the same value
    setSourceNodeAndNewNodeBeingAdded(null);

    const isPlaceholder = isInteractivePlaceholderNode(newNode);
    const edge = buildControlFlowEdge({
      sourceNodeId,
      targetNodeId: newNode.id,
    });

    const sidepanelChange: SidepanelChange = isPlaceholder
      ? {
          sidepanel: SidepanelType.NODE_SETTINGS,
          nodeId: newNode.id,
        }
      : {sidepanel: null};

    sidepanelManager.changeSidepanel({
      sidepanelChange,
      onBeforeSidepanelChanged: async () => {
        await applyFlowchartChanges(
          (flowchartDraftState) => ({
            nodeChanges: [
              {
                type: 'add',
                item: {
                  ...newNode,
                  data: {
                    ...newNode.data,
                    getAddNodeOptionsForRightHandle,
                  },
                },
              },
            ],
            edgeChanges: [
              {
                type: 'add',
                item: edge,
              },
            ],
          }),
          false,
          isPlaceholder
        );
        if (isPlaceholder) {
          setPlaceholderNodeBeingAdded(newNode.id);
        }
        setSourceNodeAndNewNodeBeingAdded(null);
      },
    });
  }, [
    _sourceNodeAndNewNodeBeingAdded,
    setSourceNodeAndNewNodeBeingAdded,
    applyFlowchartChanges,
    getAddNodeOptionsForRightHandle,
    sidepanelManager,
    setPlaceholderNodeBeingAdded,
  ]);

  // Handle opening sidepanel for placeholder nodes
  useEffect(() => {
    if (placeholderNodeIdBeingAdded != null) {
      // Check if the node actually exists in interactiveNodes
      const nodeExists = interactiveNodes.some((node) => node.id === placeholderNodeIdBeingAdded);

      if (!nodeExists) {
        // Node hasn't been added to interactiveNodes yet, wait for next render
        return;
      }

      // Select the placeholder node
      applyFlowchartChanges((flowchartDraftState) => ({
        nodeChanges: [
          {
            type: 'select',
            id: placeholderNodeIdBeingAdded,
            selected: true,
          },
        ],
      }));
      // Open the sidepanel after a brief delay to ensure the getNodeById ref is updated
      setTimeout(() => {
        openSidepanelForNode(placeholderNodeIdBeingAdded);
      }, 0);
      setPlaceholderNodeBeingAdded(null);
    }
  }, [
    placeholderNodeIdBeingAdded,
    openSidepanelForNode,
    setPlaceholderNodeBeingAdded,
    applyFlowchartChanges,
    interactiveNodes,
  ]);

  // Should only be used for reactflow-specific node changes
  // When the FlowchartConfigWithMetadata can't be used to generate changes
  const onReactFlowNodeChangesOnly = useCallback(
    (nodeChanges: Array<FlowchartNodeChange>) => {
      const positionChanges = nodeChanges.filter((c) => c.type === FlowchartNodeChangeType.POSITION);
      // We ignore dimension changes (height and width of a node). Autolayout is the only code that relies on this as of this writing.
      // However, dimensions don't seem to propagate from reactflow when the flowchart is locked. Instead,
      // we expect autolayout code to grab the dimensions from the node internals on its own.
      const otherChanges = nodeChanges.filter(
        (c) => c.type !== FlowchartNodeChangeType.POSITION && c.type !== FlowchartNodeChangeType.DIMENSIONS
      );

      if (positionChanges.length > 0) {
        // Handle position changes locally using ReactFlow's applyNodeChanges
        // This avoids triggering Redux updates and full flowchart rebuilds during dragging
        setInteractiveNodes((nodes) => {
          const updatedNodes = applyNodeChanges(positionChanges, nodes);
          updatedNodes.forEach((node: Node) => {
            currentNodePositionsRef.current[node.id] = node.position;
          });
          return updatedNodes;
        });

        // Also update local position state
        positionChanges.forEach((change) => {
          if (change.position) {
            setNodePositions((prevPositions) => ({
              ...prevPositions,
              [change.id]: change.position as {x: number; y: number},
            }));
          }
        });
      }

      if (otherChanges.length > 0) {
        applyFlowchartChanges((flowchartConfig) => ({nodeChanges: otherChanges, edgeChanges: []}));
      }
    },
    [applyFlowchartChanges]
  );

  // Should only be used for reactflow-specific edge changes
  // When the FlowchartConfigWithMetadata can't be used to generate changes
  const onReactFlowEdgeChangesOnly = useCallback(
    (edgeChanges: Array<FlowchartEdgeChange>) => {
      applyFlowchartChanges((flowchartConfig) => ({nodeChanges: [], edgeChanges}));
    },
    [applyFlowchartChanges]
  );

  // Track the previous sidepanel state to determine if it has changed
  const previousSidepanelStateRef = useRef<{nodeId: string | null; fieldId: string | null}>({
    nodeId: null,
    fieldId: null,
  });

  // Handle deselecting nodes and rows when sidepanel closes
  useEffect(() => {
    const effectiveState = sidepanelManager.getEffectiveState();
    const previousState = previousSidepanelStateRef.current;
    const currentNodeId = effectiveState.node?.id ?? null;
    const currentFieldId = effectiveState.fieldId;

    // Only act if state actually changed
    if (previousState.nodeId === currentNodeId && previousState.fieldId === currentFieldId) {
      return;
    }

    // Sidepanel closed: node went from non-null to null
    if (previousState.nodeId != null && currentNodeId == null) {
      const nodeIdToDeselect = previousState.nodeId;
      const nodeChanges: Array<FlowchartNodeChange> = [];

      nodeChanges.push({
        type: FlowchartNodeChangeType.SELECT,
        id: nodeIdToDeselect,
        selected: false,
      });

      // Convert selected field id to row id and deselect the row
      if (previousState.fieldId != null) {
        const row = staticFlowchart.nodesStaticData
          .find(
            (node): node is TaskSchemaStaticNodeData =>
              isStaticTaskSchemaNodeData(node) && node.id === nodeIdToDeselect
          )
          ?.sections.flatMap((section) => section.sectionRows)
          .find((row) => row.clientId === previousState.fieldId);

        if (row != null) {
          nodeChanges.push({
            type: FlowchartNodeChangeType.SELECT_ROW,
            id: nodeIdToDeselect,
            rowId: row.id,
            selected: false,
          });
        }
      }

      applyFlowchartChanges((flowchartConfig) => ({nodeChanges}));
    }

    previousSidepanelStateRef.current = {
      nodeId: currentNodeId,
      fieldId: currentFieldId,
    };
  }, [sidepanelManager, staticFlowchart.nodesStaticData, applyFlowchartChanges]);

  const onConnect = useCallback(
    (connection: Connection): void => {
      if (
        connection.source != null &&
        connection.target != null &&
        connection.sourceHandle != null &&
        connection.targetHandle != null
      ) {
        const sourceHandleType = getEdgeTypeFromHandleId(connection.sourceHandle);
        const targetHandleType = getEdgeTypeFromHandleId(connection.targetHandle);

        const edgeType =
          sourceHandleType === targetHandleType ? sourceHandleType : ProcessFlowchartEdgeType.CONTROL_FLOW;

        analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.EDGE_DRAWN, {
          actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
          edgeType,
          flowchartType: flowchartTypeName,
        });

        applyFlowchartChanges((flowchartConfig) => ({
          edgeChanges: [
            {
              type: FlowchartEdgeChangeType.ADD,
              item: {
                id:
                  edgeType === ProcessFlowchartEdgeType.CONTROL_FLOW
                    ? generateDeterministicEdgeId(connection.source!, connection.target!)
                    : uuidv4(),
                source: connection.source!,
                target: connection.target!,
                sourceHandle: connection.sourceHandle,
                targetHandle: connection.targetHandle,
                type: edgeType,
              },
            },
          ],
        }));
      }
    },
    [applyFlowchartChanges, flowchartTypeName]
  );

  const setNodesAndEdgesViaChangesForLayout = useCallback(
    (newNodes: Array<Node>, newEdges: Array<Edge>): void => {
      // Use unstable_batchedUpdates to ensure both state updates happen in a single render cycle.
      // NOTE: This is not necessary in React 18
      // Without this, updating nodes and edges separately can cause an interstitial rebuild
      // to fire between the two updates
      // Note: unstable_batchedUpdates is actually quite stable. https://stackoverflow.com/a/48610973
      unstable_batchedUpdates(() => {
        // Update node positions
        const nodeChanges: Array<FlowchartNodeChange> = newNodes.map((node) => ({
          type: FlowchartNodeChangeType.POSITION,
          id: node.id,
          position: node.position,
        }));
        onReactFlowNodeChangesOnly(nodeChanges);

        // Update edge layout data
        const newElkEdgeDataByEdgeId = newEdges.reduce(
          (acc, e) => {
            acc[e.id] = e.data.elkEdgeData;
            return acc;
          },
          {} as Record<string, ElkEdgeData>
        );
        setElkEdgeDataByEdgeId(newElkEdgeDataByEdgeId);
      });
    },
    [setElkEdgeDataByEdgeId, onReactFlowNodeChangesOnly]
  );

  const handleDeleteKeyPress = useCallback(
    (event: KeyboardEvent): void => {
      if (locked || !keyboard.isADeleteKey(event) || isFocusSupercededByModal()) {
        return;
      }

      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const selectedEdges = interactiveEdges.filter((edge) => edge.selected);
      const selectedNodes = interactiveNodes.filter((node) => node.selected);

      if (selectedEdges.length === 0 && selectedNodes.length === 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      // If only edges are selected, delete them immediately without confirmation
      if (selectedEdges.length > 0 && selectedNodes.length === 0) {
        const edgeChanges = selectedEdges.map((edge) => ({
          type: 'remove' as const,
          id: edge.id,
        }));
        onReactFlowEdgeChangesOnly(edgeChanges);
        return;
      }

      // If nodes are selected (with or without edges), show confirmation modal
      if (selectedNodes.length > 0) {
        const deletableNodes = selectedNodes.filter((node) => node.deletable !== false);

        if (deletableNodes.length === 0) {
          return;
        }

        const nodeNames = deletableNodes.map((node) => node.data.nodeName);

        const edgeChanges =
          selectedEdges.length > 0
            ? selectedEdges.map((edge) => ({
                type: 'remove' as const,
                id: edge.id,
              }))
            : [];

        const nodeChanges = deletableNodes.map((node) => ({
          type: 'remove' as const,
          id: node.id,
        }));

        runModal(FlowchartRemoveNodeModal, {
          nodeNames,
          onRequestRemove: () => {
            sidepanelManager.forceClose();

            if (edgeChanges.length > 0) {
              onReactFlowEdgeChangesOnly(edgeChanges);
            }

            onReactFlowNodeChangesOnly(nodeChanges);
          },
        });
      }
    },
    [
      locked,
      interactiveNodes,
      interactiveEdges,
      onReactFlowEdgeChangesOnly,
      onReactFlowNodeChangesOnly,
      sidepanelManager,
    ]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleDeleteKeyPress);
    return () => {
      document.removeEventListener('keydown', handleDeleteKeyPress);
    };
  }, [handleDeleteKeyPress]);

  return {
    // State
    interactiveNodes,
    interactiveEdges,
    setInteractiveNodes,
    // Core functions
    applyFlowchartChanges,
    onReactFlowNodeChangesOnly,
    onReactFlowEdgeChangesOnly,
    onConnect,
    setNodesAndEdgesViaChangesForLayout,
    // Sidepanel state
    isLeftSidepanelOpen,
    // Node addition state
    interactiveFlowchartOptions,
    setPlaceholderNodeBeingAdded,
    // Node dimensions
    getMeasuredDimensionsFromStore,
  };
};

export default useFlowchartState;
